import { memo, useMemo, useState } from "react";
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import { SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { Kind, Node, Space, View, ViewFilters } from "@melo/shared";
import { store, useTables } from "../lib/store";
import { byRank, childrenOf, descendantIds, formatDate, isDone, keyOf, statusOf, todayIso } from "../lib/select";
import { moveNode, send, updateNode } from "../lib/actions";
import { openItem } from "../lib/nav";
import { canEditNode } from "../lib/access";
import { isSelecting, selectClick, useSelection } from "../lib/selection";
import { AssigneePicker, DateInput, StatusDot, StatusPicker } from "../components/pickers";
import { LabelChips } from "../components/labels";
import { BulkBar } from "../components/BulkBar";
import { Empty } from "../components/primitives";
import { Icon } from "../components/icons";
import { lower, milestoneWord } from "../lib/words";
import { columnsFor, newItemParent, useViewNodes, type Column } from "./common";

type Layout = Record<string, string[]>;

/** Milestone groups show every milestone in the project, even empty ones, in roadmap order. */
function groupsFor(t: ReturnType<typeof useTables>, space: Space, view: View, kinds: Kind[], nodes: Node[], scope?: string): { columns: Column[]; columnOf: (n: Node) => string } {
  if (view.config.groupBy !== "parent") return columnsFor(t, view, kinds, nodes);
  // Scoped to a milestone, the groups are that milestone and any sprints inside it.
  const inScope = scope ? descendantIds(t, scope) : null;
  const phases = [...t.nodes.values()].filter((n) => n.spaceId === space.id && !n.draft && t.kinds.get(n.kindId)?.key === "phase" && (!scope || n.id === scope || inScope!.has(n.id))).sort((a, b) => (a.startDate ?? a.dueDate ?? "9999").localeCompare(b.startDate ?? b.dueDate ?? "9999") || byRank(a, b));
  const shownIds = new Set(nodes.map((n) => n.id));
  const others = [...new Set(nodes.map((n) => n.parentId).filter((p): p is string => Boolean(p) && !shownIds.has(p!) && !phases.some((ph) => ph.id === p)))]
    .map((id) => t.nodes.get(id))
    .filter((n): n is Node => Boolean(n));
  return {
    columns: [
      ...phases.map((p) => ({ key: p.id, label: p.title, change: () => ({ parentId: p.id }) })),
      ...others.map((p) => ({ key: p.id, label: p.title, change: () => ({ parentId: p.id }) })),
      ...(scope ? [] : [{ key: "", label: `No ${lower(milestoneWord(t, space.id))}`, change: () => ({ parentId: null }) }]),
    ],
    columnOf: (n) => n.parentId ?? "",
  };
}

export function List({ space, view, filters }: { space: Space; view: View; filters: ViewFilters }) {
  const t = useTables();
  const { nodes, kinds } = useViewNodes(t, space, view, filters);
  const top = useMemo(() => (view.config.showSubtasks ? nodes.filter((n) => !n.parentId || !nodes.some((m) => m.id === n.parentId)) : nodes), [nodes, view.config.showSubtasks]);
  const { columns, columnOf } = useMemo(() => groupsFor(t, space, view, kinds, top, filters.milestone), [t, space, view, kinds, top, filters.milestone]);
  const base = useMemo(() => {
    const out: Layout = Object.fromEntries(columns.map((c) => [c.key, [] as string[]]));
    for (const n of top) (out[columnOf(n)] ?? out[""])?.push(n.id);
    return out;
  }, [columns, columnOf, top]);
  const [drag, setDrag] = useState<{ id: string; from: string; layout: Layout } | null>(null);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const layout = drag?.layout ?? base;
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));
  const selection = useSelection();
  // Subtasks shown under their parents, when the view asks for them.
  const subsOf = (id: string) => (view.config.showSubtasks ? nodes.filter((n) => n.parentId === id).sort(byRank) : []);
  // Visual order, for shift-click ranges: each row, then its subtasks.
  const order = columns.flatMap((c) => (collapsed.has(c.key) ? [] : (layout[c.key] ?? []).flatMap((id) => [id, ...subsOf(id).map((x) => x.id)])));
  const containerOf = (id: string, l: Layout) => (id in l ? id : Object.keys(l).find((k) => l[k]!.includes(id)));
  const kindKey = kinds.find((k) => k.key === "task")?.key ?? kinds[0]?.key;

  const onStart = (e: DragStartEvent) => setDrag({ id: String(e.active.id), from: containerOf(String(e.active.id), base) ?? "", layout: base });
  const onOver = (e: DragOverEvent) => {
    if (!drag || !e.over) return;
    const overId = String(e.over.id);
    const from = containerOf(drag.id, drag.layout);
    const to = containerOf(overId, drag.layout);
    if (from === undefined || to === undefined || from === to) return;
    const node = store.t.nodes.get(drag.id);
    const col = columns.find((c) => c.key === to);
    if (!node || !col?.change?.(node)) return;
    const next = { ...drag.layout, [from]: drag.layout[from]!.filter((x) => x !== drag.id) };
    const target = [...drag.layout[to]!];
    target.splice(overId in drag.layout ? target.length : Math.max(0, target.indexOf(overId)), 0, drag.id);
    next[to] = target;
    setDrag({ ...drag, layout: next });
  };
  const onEnd = (e: DragEndEvent) => {
    const d = drag;
    setDrag(null);
    if (!d || !e.over) return;
    const node = store.t.nodes.get(d.id);
    if (!node) return;
    const to = containerOf(d.id, d.layout)!;
    const list = [...d.layout[to]!];
    const overId = String(e.over.id);
    if (!(overId in d.layout) && overId !== d.id) {
      const a = list.indexOf(d.id);
      const b = list.indexOf(overId);
      if (a >= 0 && b >= 0) list.splice(b, 0, ...list.splice(a, 1));
    }
    const i = list.indexOf(d.id);
    const crossed = to !== d.from;
    if (!crossed && base[to]!.indexOf(d.id) === i) return;
    const col = columns.find((c) => c.key === to)!;
    const change = crossed ? (col.change?.(node) ?? null) : {};
    if (crossed && !change) return;
    const after = list[i - 1];
    const before = list[i + 1];
    void moveNode(node, { ...(after ? { afterId: after } : before ? { beforeId: before } : {}), ...change }, crossed ? `${keyOf(store.t, node)} moved to ${col.label}` : undefined);
  };

  const active = drag ? t.nodes.get(drag.id) : undefined;
  return (
    <div className="list-wrap">
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={onStart} onDragOver={onOver} onDragEnd={onEnd} onDragCancel={() => setDrag(null)}>
        {columns.map((c) => {
          const ids = layout[c.key] ?? [];
          const hidden = collapsed.has(c.key);
          const phase = c.key ? t.nodes.get(c.key) : undefined;
          const doneCount = ids.filter((id) => {
            const n = t.nodes.get(id);
            return n && isDone(t, n);
          }).length;
          return (
            <section key={c.key} className="lgroup" aria-label={c.label}>
              <header className="lgroup-h">
                <button type="button" className="lgroup-toggle" aria-expanded={!hidden} onClick={() => setCollapsed((s) => new Set(s.has(c.key) ? [...s].filter((x) => x !== c.key) : [...s, c.key]))}>
                  <Icon name={hidden ? "chev" : "down"} />
                  {c.status && <StatusDot status={c.status} />}
                  {c.color && <span className="label-dot" style={{ background: c.color }} />}
                  <span className="lgroup-title">{c.label}</span>
                  <span className="n">{ids.length}</span>
                </button>
                {phase && (
                  <span className="lgroup-meta">
                    {phase.startDate || phase.dueDate ? `${formatDate(phase.startDate)}${phase.startDate && phase.dueDate ? " – " : ""}${formatDate(phase.dueDate)}` : ""}
                    {ids.length > 0 && (
                      <span className="meter" style={{ width: 80, display: "inline-block", marginLeft: 10 }}>
                        <i style={{ width: `${(doneCount / ids.length) * 100}%` }} />
                      </span>
                    )}
                  </span>
                )}
                {phase && (
                  <button type="button" className="btn ghost sm" onClick={() => openItem(keyOf(t, phase))}>
                    Open
                  </button>
                )}
              </header>
              {!hidden && (
                <GroupBody column={c} ids={ids} dragging={drag?.id} selection={selection} order={order} subsOf={subsOf} space={space} view={view} kindKey={kindKey} />
              )}
            </section>
          );
        })}
        {!top.length && !columns.length && <Empty title="Nothing matches." />}
        <DragOverlay>{active ? <div className="lrow dragging-card"><RowContent node={active} /></div> : null}</DragOverlay>
      </DndContext>
      <BulkBar spaceId={space.id} kinds={kinds} />
    </div>
  );
}

function GroupBody({ column, ids, dragging, selection, order, subsOf, space, view, kindKey }: { column: Column; ids: string[]; dragging?: string; selection: Set<string>; order: string[]; subsOf: (id: string) => Node[]; space: Space; view: View; kindKey?: string }) {
  const t = useTables();
  const { setNodeRef } = useDroppable({ id: column.key });
  return (
    <SortableContext id={column.key} items={ids} strategy={verticalListSortingStrategy}>
      <div ref={setNodeRef} className="lgroup-b">
        {ids.map((id) => {
          const n = t.nodes.get(id);
          if (!n) return null;
          return (
            <div key={id}>
              <SortableRow node={n} hidden={dragging === id} selected={selection.has(id)} order={order} />
              {subsOf(id).map((s) => (
                <SubRow key={s.id} node={s} selected={selection.has(s.id)} order={order} />
              ))}
            </div>
          );
        })}
        {!ids.length && <div className="lgroup-empty">Drop here</div>}
        {column.change && kindKey && <AddRow space={space} view={view} column={column} kindKey={kindKey} lastId={ids.at(-1)} />}
      </div>
    </SortableContext>
  );
}

const SortableRow = memo(function SortableRow({ node, hidden, selected, order }: { node: Node; hidden: boolean; selected: boolean; order: string[] }) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: node.id });
  return (
    <div
      ref={setNodeRef}
      className={`lrow${hidden ? " placeholder" : ""}${selected ? " selected" : ""}`}
      data-node={node.id}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      onClick={(e) => {
        if ((e.target as HTMLElement).closest("button, input, .date-input")) return;
        if (e.metaKey || e.ctrlKey || e.shiftKey || isSelecting()) selectClick(node.id, order, e);
        else openItem(keyOf(store.t, node));
      }}
    >
      <span className="grip" {...attributes} {...listeners} aria-label={`Reorder ${node.title}`}>
        <Icon name="grip" />
      </span>
      <button type="button" className={`card-check static${selected ? " on" : ""}`} aria-label={selected ? `Deselect ${node.title}` : `Select ${node.title}`} aria-pressed={selected} onClick={(e) => (e.stopPropagation(), selectClick(node.id, order, e))} />
      <RowContent node={node} />
    </div>
  );
});

function SubRow({ node, selected, order }: { node: Node; selected: boolean; order: string[] }) {
  return (
    <div
      className={`lrow sub-row${selected ? " selected" : ""}`}
      data-node={node.id}
      onClick={(e) => {
        if ((e.target as HTMLElement).closest("button, input, .date-input")) return;
        if (e.metaKey || e.ctrlKey || e.shiftKey || isSelecting()) selectClick(node.id, order, e);
        else openItem(keyOf(store.t, node));
      }}
    >
      <button type="button" className={`card-check static${selected ? " on" : ""}`} aria-label={selected ? `Deselect ${node.title}` : `Select ${node.title}`} aria-pressed={selected} onClick={(e) => (e.stopPropagation(), selectClick(node.id, order, e))} />
      <RowContent node={node} />
    </div>
  );
}

function RowContent({ node }: { node: Node }) {
  const t = useTables();
  const kind = t.kinds.get(node.kindId);
  const kids = childrenOf(t, node.id);
  const done = kids.filter((k) => isDone(t, k)).length;
  const editable = canEditNode(node);
  const late = node.dueDate && node.dueDate < todayIso() && statusOf(kind, node.status)?.category !== "done";
  const priority = typeof node.fields.priority === "string" ? node.fields.priority : null;
  return (
    <>
      <span className="lrow-status" onClick={(e) => e.stopPropagation()}>
        {editable ? <StatusPicker node={node} kind={kind} /> : <span className="chip"><StatusDot status={statusOf(kind, node.status)} /></span>}
      </span>
      <span className="mono muted lrow-key">{keyOf(t, node)}</span>
      <span className="lrow-title">
        {node.title}
        {node.blocker && <span className="chip sig">{node.blocker.kind === "needs_input" ? "Needs input" : "Blocked"}</span>}
      </span>
      <LabelChips ids={node.labels} />
      <span className="lrow-subs">
        {kids.length > 0 && (
          <span className="sub-prog" title={`${done} of ${kids.length} subtasks done`}>
            <span className="sub-bar">
              <i style={{ width: `${(done / kids.length) * 100}%` }} />
            </span>
            <span className="mono">
              {done}/{kids.length}
            </span>
          </span>
        )}
      </span>
      <span className="lrow-pri">{priority && (priority === "urgent" || priority === "high") && <span className={`chip ${priority === "urgent" ? "err" : "warn"}`}>{priority[0]!.toUpperCase() + priority.slice(1)}</span>}</span>
      <span className={`lrow-due${late ? " late" : ""}`} onClick={(e) => e.stopPropagation()}>
        {editable && store.myRoles[node.spaceId] !== "contractor" ? <DateInput label="Due" placeholder="–" value={node.dueDate} onSave={(v) => void updateNode(node, { dueDate: v })} /> : node.dueDate ? formatDate(node.dueDate) : ""}
      </span>
      <span className="lrow-who" onClick={(e) => e.stopPropagation()}>
        <AssigneePicker node={node} compact />
      </span>
    </>
  );
}

function AddRow({ space, view, column, kindKey, lastId }: { space: Space; view: View; column: Column; kindKey: string; lastId?: string }) {
  const [title, setTitle] = useState("");
  const add = () => {
    const text = title.trim();
    if (!text) return;
    setTitle("");
    const probe = { labels: [], assignees: [] } as unknown as Node;
    const change = column.change?.(probe) ?? {};
    void send("node.create", {
      spaceId: space.id,
      nodes: [
        {
          kind: kindKey,
          title: text,
          parentId: change.parentId !== undefined ? change.parentId : newItemParent(view),
          status: change.status,
          fields: change.fields ?? undefined,
          assignees: change.assignees,
          labels: change.labels,
          ...(lastId ? { afterId: lastId } : {}),
        },
      ],
    });
  };
  return (
    <div className="lrow add-row">
      <Icon name="plus" />
      <input className="inline-input" placeholder={column.key === "" || column.key === "all" ? "Add a task" : `Add to ${column.label}`} value={title} onChange={(e) => setTitle(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} aria-label={`Add a task to ${column.label}`} />
    </div>
  );
}
